// app/hooks/useConsentCheck.ts
'use client'

import { useEffect, useState, useCallback } from 'react';
// Stelle sicher, dass ConsentState explizit importiert wird (oder definiere es hier passend)
import { useConsent, ConsentState } from '@/app/contexts/ConsentContext'; // Wichtig für consents[category] Typisierung

// Definiere die Service-Typen, die der Hook akzeptiert
type ServiceType = 'facebook' | 'googleAds' | 'posthog';

// Definiere die Consent-Kategorien, auf die gemappt wird (müssen Schlüssel in ConsentState sein)
type MappedConsentCategory = keyof Omit<ConsentState, 'technical'>; // Ergibt aktuell 'marketing'


/**
 * Custom hook to check consent for a specific service ('facebook', 'googleAds', 'posthog').
 * Returns consent status and initialization status.
 * Liest den Status aus dem ConsentContext und reagiert optional auf 'consent-updated' Events.
 * Sendet KEINE Updates mehr an gtag (das macht ConsentProvider).
 */
export function useConsentCheck(serviceType: ServiceType) {
  const { consents, isInitialized } = useConsent();
  // Lokaler State, um den Consent-Status für diesen spezifischen Service zu halten
  const [hasConsent, setHasConsent] = useState(false);

  // Interne Funktion, um Service auf Kategorie zu mappen
  // Gibt einen Typ zurück, der garantiert ein Schlüssel von ConsentState ist (oder null)
  const getCategory = useCallback((): MappedConsentCategory | null => {
    switch (serviceType) {
      case 'facebook':
      case 'googleAds':
      case 'posthog':
        return 'marketing';
      // Füge hier ggf. weitere Mappings hinzu
      default:
        // Stellt sicher, dass alle ServiceType-Fälle behandelt werden (optional)
        // const _exhaustiveCheck: never = serviceType;
        return null;
    }
  }, [serviceType]); // Abhängigkeit von serviceType

  // Effekt 1: Aktualisiert den lokalen hasConsent-State, wenn sich der Context ändert
  useEffect(() => {
    if (isInitialized) {
      const category = getCategory();
      // Prüfen, ob eine Kategorie gefunden wurde und diese im consents-Objekt existiert
      if (category /* && category in consents */) { // 'category in consents' ist optional, da MappedConsentCategory schon ein Key ist
        // Setze den lokalen State basierend auf dem Wert aus dem Context
        setHasConsent(!!consents[category]);
      } else {
        setHasConsent(false); // Kein Consent, wenn keine Kategorie gefunden wurde
      }
    } else {
      setHasConsent(false); // Kein Consent, solange nicht initialisiert
    }
    // Abhängigkeiten: Context-Werte und die abgeleitete Kategorie (via serviceType)
  }, [isInitialized, consents, serviceType, getCategory]); // getCategory als Abhängigkeit hinzugefügt


  // Effekt 2: Hört auf das benutzerdefinierte Event für sofortige Updates (optional aber hier vorhanden)
  useEffect(() => {
    const handleConsentUpdate = (e: CustomEvent) => {
      // Leite die Kategorie für diesen Hook ab
      const category = getCategory();
      // Prüfe, ob das Event Details hat, ein Objekt ist und die relevante Kategorie enthält
      if (category && e.detail && typeof e.detail === 'object' && category in e.detail) {
        // Aktualisiere den lokalen State sofort basierend auf dem Event
        // Wir brauchen einen Type Guard oder eine Assertion, da e.detail 'any' ist
        const consentValue = (e.detail as ConsentState)[category];
        console.log(`[useConsentCheck][${serviceType}] Event 'consent-updated' received for category '${category}'. New value: ${!!consentValue}`);
        setHasConsent(!!consentValue);
      }
    };

    // Event Listener hinzufügen (wenn window verfügbar)
    if (typeof window !== 'undefined') {
      window.addEventListener('consent-updated', handleConsentUpdate as EventListener);
      console.log(`[useConsentCheck][${serviceType}] Added event listener for 'consent-updated'.`);
    }

    // Cleanup: Event Listener entfernen
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('consent-updated', handleConsentUpdate as EventListener);
        console.log(`[useConsentCheck][${serviceType}] Removed event listener for 'consent-updated'.`);
      }
    };
  }, [serviceType, getCategory]); // Abhängigkeit von serviceType (und somit getCategory)


  // Effekt 3 (Der für gtag('consent', 'update', ...)) wird hier *nicht* mehr eingefügt.

  // Gib den lokalen State und den Initialisierungsstatus zurück
  return { hasConsent, isInitialized };
}